import { ACQUISITION_TTL } from './acquisition';

export const ATTACH_MEMORY_KEY = 'talktime.acquisition.attach.v1';
const ATTACH_MEMORY_LIMIT = 50;

export type SettledAttachReason = 'mismatch' | 'unavailable' | 'limit';

export interface AttachStorage {
   getItem(key: string): string | null;
   setItem(key: string, value: string): void;
}

type SettledEntry = { reason: SettledAttachReason; at: number };

const pairKey = (account: string, journey: string) => `${account}|${journey}`;

function read(storage: AttachStorage | null, now: number): Record<string, SettledEntry> {
   if (!storage) return {};
   try {
      const parsed: unknown = JSON.parse(storage.getItem(ATTACH_MEMORY_KEY) || '{}');
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
      const entries: Record<string, SettledEntry> = {};
      for (const [key, value] of Object.entries(parsed)) {
         const entry = value as Partial<SettledEntry> | null;
         if (!entry || typeof entry.at !== 'number' || typeof entry.reason !== 'string') continue;
         // Only the server decides whether an account may use an offer, so a
         // stored 'own_offer' entry does not settle the journey.
         if ((entry.reason as string) === 'own_offer') continue;
         if (now - entry.at > ACQUISITION_TTL || entry.at > now + 60000) continue;
         entries[key] = { reason: entry.reason as SettledAttachReason, at: entry.at };
      }
      return entries;
   } catch {
      return {};
   }
}

function write(storage: AttachStorage | null, entries: Record<string, SettledEntry>) {
   if (!storage) return;
   const newest = Object.entries(entries)
      .sort(([, left], [, right]) => right.at - left.at)
      .slice(0, ATTACH_MEMORY_LIMIT);
   try {
      storage.setItem(ATTACH_MEMORY_KEY, JSON.stringify(Object.fromEntries(newest)));
   } catch {
      // Storage can be full or blocked; the in-memory guard still covers this page load.
   }
}

// A settled pair is never attached again and never shows another notice, so a
// reload or a later sign-in to the same account stays quiet. Other accounts in
// the same browser keep their own memory.
export function settledAttachReason(
   storage: AttachStorage | null, account: string, journey: string, now = Date.now(),
): SettledAttachReason | undefined {
   return read(storage, now)[pairKey(account, journey)]?.reason;
}

export function settleAttach(
   storage: AttachStorage | null, account: string, journey: string,
   reason: SettledAttachReason, now = Date.now(),
) {
   const entries = read(storage, now);
   entries[pairKey(account, journey)] = { reason, at: now };
   write(storage, entries);
}

// A fresh redemption of the same journey is a new request to attach it.
export function forgetAttachJourney(storage: AttachStorage | null, journey: string, now = Date.now()) {
   const entries = read(storage, now);
   for (const key of Object.keys(entries)) {
      if (key.endsWith(`|${journey}`)) delete entries[key];
   }
   write(storage, entries);
}

// Nuxt auto-imports the app's utils/localStorage wrapper under the name
// `localStorage`, which JSON-encodes every value; read the browser's own store.
export function browserAttachStorage(): AttachStorage | null {
   try {
      return typeof window === 'undefined' ? null : window.localStorage;
   } catch {
      return null;
   }
}
